// Global Instructions Rule Applied!
//
// Configuration → Email Categories services: the aggregate admin read, the
// per-row save, the precedence reorder, and the categorization settings.
//
// Same fetch conventions as internalAgentsConfigService. Every route is
// admin-gated at the backend AND inside the RPC, so a 403 surfaces as the
// readable "Administrator access required" from the body. Validation refusals
// (an unknown setting key, a value out of range, a partial reorder) come back
// 400 with the setter's own message — those are designed, user-facing outcomes,
// not errors, and the panel shows them next to the control.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  EmailCategoriesConfigAdminData,
  EmailCategoryAdminRow,
  EmailCategorySetting,
  EmailRecipientMarker,
  validateEmailCategoryAdminRow,
  validateEmailCategorySetting,
  validateEmailRecipientMarker
} from '../../../state_manager/data_models/email_categories_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// Writes use the house request envelope { data: {...} } — the /config/* routes
// unwrap it via _json_body, and a flat body is silently dropped (#232).
async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    // The backend's error envelope key is `message` (handle_route_custom_error);
    // `error` is the auth guard's. Reading `message` FIRST is what makes the
    // 403 and the validation refusals surface readably (#232).
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchEmailCategoriesConfig = async (
  accessToken: string | null
): Promise<EmailCategoriesConfigAdminData> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.EMAIL_CATEGORIES_CONFIG),
    { method: API_ROUTES.CONFIG.EMAIL_CATEGORIES_CONFIG.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const data = body.data || {}
  const categories = (data.categories || []) as EmailCategoryAdminRow[]
  const settings = (data.app_config || []) as EmailCategorySetting[]
  const recipientMarkers = (data.recipient_markers || []) as EmailRecipientMarker[]
  validateEmailCategoryAdminRow(categories, { isArray: true })
  validateEmailCategorySetting(settings, { isArray: true })
  validateEmailRecipientMarker(recipientMarkers, { isArray: true })
  return {
    resolvedEnvironment: data.resolved_environment || 'global',
    categories,
    settings,
    recipientMarkers
  }
}

export interface EmailCategoryFields {
  label?: string
  description?: string | null
  precedence?: number
  enabled?: boolean
  automate_label?: string | null
  outlook_label?: string | null
  outlook_color?: string | null
}

/** One taxonomy row. Only the supplied keys are touched — the RPC reads an
 *  absent key as "leave unchanged", which is what lets the panel autosave a
 *  single field without shipping the whole row. */
export const updateEmailCategory = async (
  accessToken: string | null,
  categoryId: string,
  fields: EmailCategoryFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.EMAIL_CATEGORY_UPDATE, { categoryId }),
    {
      method: API_ROUTES.CONFIG.EMAIL_CATEGORY_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

/** The complete precedence order, in one call. A partial list is refused: the
 *  final category is the min-precedence enabled match, so a half-applied
 *  reorder would silently refile mail. */
export const reorderEmailCategories = async (
  accessToken: string | null,
  ids: string[]
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.EMAIL_CATEGORIES_REORDER),
    {
      method: API_ROUTES.CONFIG.EMAIL_CATEGORIES_REORDER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { ids } })
    }
  )
  await readJson(response)
}

/** One categorization setting. `value` is the raw jsonb — boolean, number, or
 *  the declined-status string list. */
export const updateEmailCategorySetting = async (
  accessToken: string | null,
  configKey: string,
  value: boolean | number | string[]
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.EMAIL_CATEGORY_SETTING_UPDATE, { configKey }),
    {
      method: API_ROUTES.CONFIG.EMAIL_CATEGORY_SETTING_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { value } })
    }
  )
  await readJson(response)
}

export interface EmailRecipientMarkerFields {
  label?: string
  outlook_color?: string | null
  enabled?: boolean
}

/** One recipient marker. Same autosave contract as the taxonomy rows: only the
 *  supplied keys are touched, the RPC reads an absent key as "leave
 *  unchanged". */
export const updateEmailRecipientMarker = async (
  accessToken: string | null,
  markerKey: string,
  fields: EmailRecipientMarkerFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.EMAIL_RECIPIENT_MARKER_UPDATE, { markerKey }),
    {
      method: API_ROUTES.CONFIG.EMAIL_RECIPIENT_MARKER_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}
